import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    Upload,
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiRequest } from '../../../service/apiRequest';
import { showSuccess, showError } from '../../../components/errorHandler';
import { BSs } from "../../../types/BodyShape";
const { Option } = Select;

interface BsModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: BSs | null;
    isEditMode?: boolean;
    bodyshapetypedata: BSs[];
}

const BsModal: React.FC<BsModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    bodyshapetypedata,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [profileImageFileList, setProfileImageFileList] = useState<UploadFile[]>([]);


    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');


    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // image preview 
    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as File);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
    };

    // Utility function to convert image to base64
    const getBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });


    // Handle form submit
    const handleAddFt = async (values: any) => {
        try {
            const formData = new FormData();

            formData.append('bs_type', values.bs_type);
            formData.append('bs_name', values.bs_name);

            if (imageFile) {
                formData.append('attachments', imageFile); // ✅ binary file
            }

            if (initialValues?.bs_id) {
                formData.append('bs_id', initialValues.bs_id.toString());
            }
            const url = isEditMode
                ? 'edit-body-shape'
                : 'add-body-shape';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            console.log(formData);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Body shape type updated successfully!' : 'Body shape created successfully!',
            );
            form.resetFields();
            if (!isModalClose) {
                setImageFile(null);
                setProfileImageFileList([]);
            }

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} body shape`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                bs_type: initialValues.bs_type,
                bs_name: initialValues.bs_name,
            });
            if (initialValues.bs_image) {
                const imageUrl = `${initialValues.bs_image}`;
                setProfileImageFileList([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: imageUrl,
                    },
                ]);
                setImageFile(null); // prevent old file being reused
            }
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);


    const uploadButton = (
        <div
            style={{
                width: 220,
                height: 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <PlusOutlined style={{ fontSize: 24 }} />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    return (
        <>
            <Modal
                title={isEditMode ? 'Edit body shape type' : 'Add New Body Shape'}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                width="90%"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleAddFt}
                    autoComplete="off"
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="bs_type"
                                label={
                                    <span>
                                        Body Shape Type &nbsp;
                                    </span>
                                }
                                rules={[{ required: true, message: 'Please select body shape type' }]}
                            >
                                {/* <Select
                                    placeholder="Select Organization"
                                    options={bodyshapetypedata.map(bs => ({ label: bs.bs_type, value: bs.bs_id }))}
                                /> */}
                                <Select
                                    placeholder="Select Body Type"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        (option?.children as unknown as string)
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                >
                                    {/* General Build */}
                                    <Option value="slim">Slim / Lean Body</Option>
                                    <Option value="average">Average Build</Option>
                                    <Option value="heavy_weight">Heavy Weight / Broad Build</Option>

                                    {/* Tummy / Waist Related */}
                                    <Option value="flat_tummy">Flat Tummy</Option>
                                    <Option value="little_tummy">Little Tummy</Option>
                                    <Option value="big_tummy">Big Tummy / Belly</Option>

                                    {/* Shoulder / Waist Proportion */}
                                    <Option value="broad_shoulders">Broad Shoulders</Option>
                                    <Option value="narrow_shoulders">Narrow Shoulders</Option>
                                    <Option value="small_waist">Small Waist</Option>
                                    <Option value="wide_waist">Wide Waist</Option>
                                </Select>

                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="bs_name"
                                className='px-8'
                                label={
                                    <span>
                                        Body Shape &nbsp;
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            (Max 100 characters)
                                        </span>
                                    </span>
                                }
                                rules={[
                                    { required: true, message: 'Please enter the Body Shape Name' },
                                    { max: 100, message: 'Maximum 100 characters allowed' },
                                ]}
                            >
                                <Input
                                    showCount
                                    maxLength={100}
                                    placeholder="e.g., Body Shape "

                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Upload Body Shape Image" required>
                                <Upload
                                    beforeUpload={() => false}
                                    listType="picture-card"
                                    maxCount={1}
                                    fileList={profileImageFileList}
                                    onPreview={handlePreview} // 👈 Add this
                                    onChange={({ fileList }) => {
                                        setProfileImageFileList(fileList);

                                        const rawFile = fileList[0]?.originFileObj;
                                        if (rawFile instanceof File) {
                                            setImageFile(rawFile); // ✅ set real File here
                                        } else {
                                            setImageFile(null);
                                        }
                                    }}
                                >
                                    {profileImageFileList.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>


                    <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button onClick={onCancel}>Cancel</Button>
                            {!isEditMode && <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit & Create New Body Shape
                            </Button>}
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(true)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                visible={previewOpen}
                title={previewTitle}
                footer={null}
                centered
                onCancel={() => setPreviewOpen(false)}
            >
                <img
                    alt="Preview"
                    style={{ width: '100%' }}
                    src={previewImage}
                />
            </Modal>
        </>
    );
};

export default BsModal;
